/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Modal, Space, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

// Misc imports
import { useFetchKnowledgeBaseArticleQuery } from '../../store/knowledgeBases/knowledgeBasesApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';

// Constant declarations
const { Paragraph, Text } = Typography;

/**
 * Given an article ID, returns a button that opens a modal with a preview of the article body
 * @param {String} articleId The ID of the article to preview
 * @param {String} knowledgeBaseId The ID of the knowledge base to which the article belongs
 * @param {String} buttonText The text to display on the button
 * @param {Object} buttonProps The props to pass to the button, in accordance to Ant Design's Button component (https://ant.design/components/button)
 * @returns {JSX.Element} The button to preview the article
 */
const ArticlePreviewButton = ({ articleId, knowledgeBaseId, buttonText, ...buttonProps }) => {
  const { orgId } = useParams();
  const { t } = useTranslation();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const { currentBotLanguage } = useBotLanguage();
  const { data: article, isFetching: isFetchingArticle } = useFetchKnowledgeBaseArticleQuery({ orgId, knowledgeBaseId: knowledgeBaseId, articleId: articleId, urlParams: { includeBody: true } });
  const [ currentLanguageArticleContent, setCurrentLanguageArticleContent ] = useState();

  useEffect(() => {
    if ( article ) {
      setCurrentLanguageArticleContent(
        article.content.find((content) => content.languageCode === currentBotLanguage)
      );
    }
  }, [article, currentBotLanguage]);

  return (
    <Fragment>
      <Button
        size="large"
        type="text"
        icon={<EyeOutlined className="fs-lg" />}
        loading={isFetchingArticle}
        onClick={() => {
          setIsPreviewModalOpen(true);
        }}
        {...buttonProps}
      >
        {buttonText}
      </Button>

      <Modal
        open={isPreviewModalOpen}
        onCancel={() => setIsPreviewModalOpen(false)}
        footer={null}
        width={800}
        centered
        title={<Space><EyeOutlined /> {t('Article Preview')}</Space>}
      >
        <Paragraph type="secondary">
          {t('Body')}
        </Paragraph>

        <div
          className="bg-layout kb-article-body word-break-break-word p-4 scrollbar-custom scrollbar-custom-nohide border-radius-base overflow-y-auto"
          style={{ maxHeight: 600 }}
        >
          {
            currentLanguageArticleContent?.body ? (
              <ReactMarkdown skipHtml>
                {currentLanguageArticleContent?.body}
              </ReactMarkdown>
            ) : (
              <Text type="secondary">
                {t('(No article body)')}
              </Text>
            )
          }
        </div>
      </Modal>
    </Fragment>
  );
};

ArticlePreviewButton.propTypes = {
  articleId: PropTypes.string.isRequired,
  knowledgeBaseId: PropTypes.string.isRequired,
  buttonText: PropTypes.string
};

export default ArticlePreviewButton;
